import { forwardRef, useMemo, type ComponentRef, type Ref } from "react";

import { Divider } from "../../atoms/divider";
import { cn, platformProps } from "../../lib";
import { Box, Pressable } from "../../primitives";
import { ListItemContext } from "./list-item-context";
import type { ListItemContextValue, ListItemProps } from "./types";

// The row pads and centres its content group. The fill sits on the root, so a
// caller's fill there isn't covered. The root doesn't clip, so a trailing
// control's tap area (a Switch's, a Button's) reaches past it: a caller that
// rounds the root adds `overflow-hidden` so the press fill keeps the corners
const rowClasses = "flex w-full flex-row items-center px-spacing-lg py-spacing-md";

// Inside the group the leading slots sit at the top (DQ21), and the group is
// centred in the row, so a 44px pressable row keeps them level with one line
const groupClasses = "gap-spacing-md flex min-w-0 flex-1 flex-row items-center";

// A pressable, enabled row's feedback (DQ3), as Card's: the fill of Figma's
// List Item `.State Background` helper on hover (pointers that hover, on web)
// and while pressed, and the shared focus ring, inset since a row spans its
// container (`web:`, since css-interop drops `:focus-visible` on native)
const pressableRowClasses = [
  "web:[@media(hover:hover)]:hover:bg-luce-neutral-background-intermediate",
  "active:bg-luce-neutral-background-intermediate",
  "web:outline-none web:focus-visible:ring-focus web:focus-visible:ring-luce-brand-primaryBorder web:focus-visible:ring-offset-focus web:focus-visible:ring-inset",
].join(" ");

// The ref goes to the pressable row when there's `onPress` (the control to
// focus), and to the root Box otherwise
export const ListItemRoot = forwardRef<ComponentRef<typeof Box>, ListItemProps>(
  function ListItemRoot(
    {
      divider = true,
      onPress,
      disabled,
      tone = "default",
      layout = "default",
      className,
      testID,
      children,
    },
    ref,
  ) {
    const pressable = !!onPress;
    const flush = layout === "flush";
    const contextValue = useMemo<ListItemContextValue>(
      () => ({ pressable, tone, disabled }),
      [pressable, tone, disabled],
    );
    const row = cn(rowClasses, flush && "p-spacing-0");
    const group = <Box className={groupClasses}>{children}</Box>;

    return (
      <ListItemContext.Provider value={contextValue}>
        <Box
          ref={pressable ? undefined : ref}
          className={cn("bg-luce-neutral-background-default", className)}
          {...platformProps({ testID, slot: "list-item" })}
        >
          {onPress ? (
            <Pressable
              ref={ref as Ref<ComponentRef<typeof Pressable>>}
              onClick={onPress}
              accessibilityRole="button"
              accessibilityState={{ disabled }}
              disabled={disabled}
              className={cn(row, "min-h-touch-target", !disabled && pressableRowClasses)}
            >
              {group}
            </Pressable>
          ) : (
            <Box className={row}>{group}</Box>
          )}

          {divider && (
            <Box className={flush ? undefined : "mx-spacing-lg"}>
              <Divider />
            </Box>
          )}
        </Box>
      </ListItemContext.Provider>
    );
  },
);

ListItemRoot.displayName = "ListItem";
